'use client'

import { useState, useEffect } from 'react'
import Pill from '@/components/ui/Pill'
import Menu from '@/components/ui/Menu'
import StackIcon from '@/components/content/stacks/shared/StackIcon'
import AppIcon from '@/components/content/app/AppIcon'
import Breadcrumb from '@/components/content/sidebar/Breadcrumb'
import ExternalIndicator from '@/components/ui/ExternalIndicator'
import PrimaryButton from '@/components/content/app/PrimaryButton'
import { Icon } from '@/icons/Icon'
import { checkAppBranding } from '@/lib/formatting'
import { usePageContext } from '@/components/content/common/PageContext'
import { getBrandColors, isPopulatedObject } from '@/lib/utils'
import { getPrimaryButton, getAppRecommendation } from '@/components/content/app/helpers'
import { determineDealsToDisplay, formatDealButton } from '@/components/content/deals/helpers'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import type { MenuItem } from '@/helpers/menus'
import type { App, AppCategoryRecommendation, Category, Deal } from '@/payload-types'

type AlternativeSidebarProps = {
  app: App
  alternativeApps: App[]
  category: Category
  menu: MenuItem[]
}

export default function AlternativeSidebar({
  app,
  alternativeApps,
  category,
  menu,
}: AlternativeSidebarProps) {
  const { activeApp } = usePageContext()
  const [displayApp, setDisplayApp] = useState<App>(app)

  useEffect(() => {
    const appToDisplay = alternativeApps.find((alt) => alt.slug === activeApp)
    setDisplayApp(appToDisplay ?? app)
  }, [activeApp, alternativeApps, app])

  const appDeals = displayApp?.deals?.docs as Deal[]
  const formattedDeals = determineDealsToDisplay(appDeals)
  const primaryDeal = formattedDeals[0]

  // Gate on the displayable (active, deduped) deals — not the raw list — so the
  // claim flag is only set when there are actually deals shown to claim.
  const hasDeals = formattedDeals.length > 0

  const handleClick = () => {
    if (!hasDeals) return
    localStorage.setItem('efficient:claim', 'true')
  }

  const { backgroundColor } = getBrandColors(displayApp)
  const { appGlyph, appIcon } = checkAppBranding(displayApp)

  // Derive the base label from the primary deal (with app-name suffix) and let
  // getPrimaryButton append "+ deal" for 2+ deals, matching the AppInfo /
  // ReviewSidebar pattern. The old count-based fallback produced awkward CTAs
  // like "Claim Deals for Notion + deal".
  const dealLabel = primaryDeal ? formatDealButton(primaryDeal, displayApp.name) : undefined
  const dealLabelShort = primaryDeal ? formatDealButton(primaryDeal) : undefined

  const categoryApps =
    (category?.appRecommendations?.docs as AppCategoryRecommendation[] | undefined)
      ?.map((rec) => {
        const app = rec.app
        return isPopulatedObject<App>(app) ? app.partnershipTier : null
      })
      .filter((tier): tier is NonNullable<typeof tier> => tier !== null) ?? []

  const { recommendation } = getAppRecommendation({
    app: displayApp,
    categoryId: category?.id ?? 0,
  })

  // lg (not md): tablets (768-1023px) are fallback users for desktop-required
  // deals, so they must not be classified as desktop.
  const { lg } = useBreakpoint()

  const primaryButton = app
    ? getPrimaryButton({
        app: displayApp,
        deal: { count: formattedDeals?.length, label: dealLabel, labelShort: dealLabelShort },
        compareWithApp: app,
        categoryApps,
        isSummary: displayApp.slug !== app.slug,
        isBest: recommendation === Recommendation.TopPick,
        isBroadCategory: Boolean(category?.isBroadCategory),
        isAlternative: true,
        isDesktop: lg,
        desktopIsRequired: primaryDeal?.desktopIsRequired ?? false,
      })
    : null

  return (
    <div className="flex w-full flex-row items-center gap-3.5 md:flex-col md:items-start md:p-4">
      <div className="flex w-full min-w-0 flex-1 items-center gap-3.5">
        {displayApp ? (
          <div className="h-8 w-8 shrink-0 md:hidden">
            <AppIcon
              glyph={appGlyph}
              icon={appIcon}
              name={displayApp.name}
              slug={displayApp.slug}
              ignoreScroll={true}
              background={backgroundColor}
              transition={false}
            />
          </div>
        ) : (
          <div className="squircle flex h-8 w-8 items-center justify-center border border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/10 md:hidden">
            <Icon name="alternative" className="text-gray-500 dark:text-white/60" />
          </div>
        )}

        <div className="hidden md:block">
          <StackIcon apps={alternativeApps.slice(0, 4)} />
        </div>

        <div className="hidden flex-col gap-0.5 leading-tight md:mt-1 md:flex lg:w-[calc(100%-70px)]">
          <span className="truncate font-semibold text-gray-950 dark:text-white">{app.name}</span>
          <span className="truncate text-13 text-gray-600 dark:text-zinc-500">
            {alternativeApps.length} alternative apps
          </span>
        </div>
        <Breadcrumb placeholder="Alternative" title={displayApp.name} />
      </div>
      <div className="flex shrink-0 gap-2.5 md:w-full">
        {primaryButton && (
          <PrimaryButton
            primaryButton={primaryButton}
            primaryDeal={primaryDeal}
            app={displayApp}
            onClick={handleClick}
            className="flex w-full"
          >
            <Pill style="primary" className="md:w-full md:justify-center">
              {primaryButton.label}
              {primaryButton.opensInNewTab && <ExternalIndicator />}
            </Pill>
          </PrimaryButton>
        )}
        {menu.length > 0 && <Menu items={menu} subAlign="down" />}
      </div>
    </div>
  )
}
